import { WIX_MINI_FILTER_EVENT_NAME } from '@va/constants';
import { useTranslate } from '@va/localization';
import { Filter } from '@va/types/filters';
import { isWixMiniApp } from '@va/util/helpers';
import { DashboardAppWorker, findFirstFocusableElement } from '@va/util/misc';
import { FormEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { useFiltersContext } from '../filters-context';
import { applyFilterValues } from '../table-filters-helpers';
import { FilterActionButtons } from './FilterActionButtons';
import { SubFilterTabHeader } from './SubFilterTabHeader';

export const IndividualFilter = ({
  filter,
  onBackClick,
  onClose,
}: {
  filter: Filter;
  onBackClick: () => void;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const { expandFilters, isFilterApplied } = useFiltersContext();
  const containerRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<{ submit: () => void }>(null);

  const Input = filter.input;
  const PremiumMarker = filter.premium?.marker;

  const appliedFilter = useMemo(() => isFilterApplied(filter.id), [filter.id, isFilterApplied]);
  const updatedFilter = useMemo(() => {
    return appliedFilter ? applyFilterValues(filter, appliedFilter) : filter;
  }, [appliedFilter, filter]);

  const handleSubmit = useCallback(() => {
    if (isWixMiniApp()) {
      const filterEventName = WIX_MINI_FILTER_EVENT_NAME[filter.name];

      if (filterEventName) {
        DashboardAppWorker.trackEvent({
          category: 'Recording Main View',
          action: 'onclick',
          label: `Add/Edit Filter ${filterEventName} Submit`,
          value: 'Button',
        });
      }
    }

    try {
      inputRef.current?.submit();
      expandFilters();
      onClose();
      // eslint-disable-next-line no-empty
    } catch {}
  }, [filter.name, expandFilters, onClose]);

  const handleFormSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleSubmit();
    },
    [handleSubmit],
  );

  useEffect(() => {
    if (!containerRef.current) return;

    const firstFocusableEl = findFirstFocusableElement(containerRef.current);
    firstFocusableEl?.focus();
  }, [filter]);

  return (
    <form className='overflow-hidden flex flex-col' ref={containerRef} onSubmit={handleFormSubmit}>
      <SubFilterTabHeader
        title={filter.label}
        onBackClick={onBackClick}
        endAdornment={PremiumMarker && <PremiumMarker pillText={translate('filters.premium.tabHeader')} />}
      />
      <Input ref={inputRef} filter={updatedFilter} {...filter?.inputProps} />
      <FilterActionButtons onClose={onClose} onSubmit={handleSubmit} isFormSubmit />
    </form>
  );
};
